import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { WorkingStateCorrectionDraft } from "../utils/hosted-agents-working-state-utils"
import type { WorkingStateFact, WorkingStateProposal } from "@/api/admin-api"

import { HostedAgentWorkingStateError } from "@/api/admin-api"
import { Bot, Check, Pencil, Pin, Trash2, User, X } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { formatFriendlyDateTime } from "@/lib/dates"

import {
  useAdminHostedAgentWorkingStateQuery,
  useConfirmWorkingStateProposalMutation,
  useCorrectWorkingStateFactMutation,
  useDeleteWorkingStateFactMutation,
  useRejectWorkingStateProposalMutation,
} from "../hooks/admin-queries"
import {
  correctionDraftFromFact,
  correctionInputFromDraft,
  formatWorkingStateValue,
} from "../utils/hosted-agents-working-state-utils"

// Working-state panel in the wizard's Memory tab (#3023). IMPORTANT UX note:
// unlike the surrounding form fields this panel mutates LIVE server documents
// — Confirm/Reject/Correct/Delete apply immediately and are NOT part of the
// dialog's Save. The intro line says so, because an admin who thinks Confirm
// still needs Save would close the dialog believing nothing happened.

function ProvenanceLine({ provenance }: { provenance: WorkingStateProposal["provenance"] }) {
  const { t } = useTranslation()
  const isAgent = provenance.actor === "agent"
  return (
    <span className="text-muted-foreground inline-flex items-center gap-1 text-[11px]">
      {isAgent ? (
        <Bot aria-hidden="true" className="size-3" />
      ) : (
        <User aria-hidden="true" className="size-3" />
      )}
      {isAgent
        ? t("adminDashboard.hostedAgents.workingState.provenanceAgent", {
            defaultValue: "Agent run {{runId}}",
            runId: provenance.run_id ?? "?",
          })
        : t("adminDashboard.hostedAgents.workingState.provenanceUser", {
            defaultValue: "Human",
          })}
      {" · "}
      {formatFriendlyDateTime(provenance.recorded_at)}
    </span>
  )
}

function WorkingStateFactCard({ agentId, fact }: { agentId: string; fact: WorkingStateFact }) {
  const { t, i18n } = useTranslation()
  const confirmMutation = useConfirmWorkingStateProposalMutation()
  const rejectMutation = useRejectWorkingStateProposalMutation()
  const correctMutation = useCorrectWorkingStateFactMutation()
  const deleteMutation = useDeleteWorkingStateFactMutation()
  const [draft, setDraft] = useState<WorkingStateCorrectionDraft | null>(null)
  const [errorText, setErrorText] = useState<string | null>(null)

  const mutationError = (error: unknown): string => {
    if (error instanceof HostedAgentWorkingStateError) {
      if (error.code === "working_state_conflict") {
        return t("adminDashboard.hostedAgents.workingState.errors.conflict", {
          defaultValue:
            "Refused: the fact is pinned by a human correction or was changed concurrently.",
        })
      }
      if (error.code === "working_state_invalid") return error.message
    }
    return t("adminDashboard.hostedAgents.workingState.errors.generic", {
      defaultValue: "The change could not be saved.",
    })
  }

  const runMutation = async (action: () => Promise<unknown>) => {
    setErrorText(null)
    try {
      await action()
    } catch (error) {
      setErrorText(mutationError(error))
    }
  }

  const submitCorrection = async () => {
    if (!draft) return
    const parsed = correctionInputFromDraft(draft)
    if ("errorKey" in parsed) {
      setErrorText(
        t(`adminDashboard.hostedAgents.workingState.errors.${parsed.errorKey}`, {
          defaultValue: "Enter a valid value and a source.",
        })
      )
      return
    }
    await runMutation(async () => {
      await correctMutation.mutateAsync({ agentId, factId: fact.id, input: parsed.input })
      setDraft(null)
    })
  }

  return (
    <div className="space-y-2 rounded-lg border p-3" data-testid="hosted-agent-working-state-fact">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs">{fact.key}</span>
        {fact.label ? <span className="text-muted-foreground text-xs">{fact.label}</span> : null}
        {fact.pinned ? (
          <Badge
            className="gap-1 px-1.5 py-0 text-[10px]"
            data-testid="hosted-agent-working-state-pinned"
            variant="secondary"
          >
            <Pin aria-hidden="true" className="size-3" />
            {t("adminDashboard.hostedAgents.workingState.pinned", {
              defaultValue: "Human-corrected",
            })}
          </Badge>
        ) : null}
        <span className="grow" />
        <Button
          data-testid="hosted-agent-working-state-correct"
          size="icon"
          type="button"
          variant="ghost"
          aria-label={t("adminDashboard.hostedAgents.workingState.correctAria", {
            defaultValue: "Correct the value",
          })}
          onClick={() => {
            setErrorText(null)
            setDraft((current) => (current ? null : correctionDraftFromFact(fact)))
          }}
        >
          <Pencil className="size-4" />
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              data-testid="hosted-agent-working-state-delete"
              size="icon"
              type="button"
              variant="ghost"
              aria-label={t("adminDashboard.hostedAgents.workingState.deleteAria", {
                defaultValue: "Delete fact",
              })}
            >
              <Trash2 className="size-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("adminDashboard.hostedAgents.workingState.deleteTitle", {
                  defaultValue: "Delete this fact?",
                })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("adminDashboard.hostedAgents.workingState.deleteBody", {
                  defaultValue:
                    "The value, its pending proposals and its history are removed permanently. The agent can propose the key again later.",
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {t("adminDashboard.hostedAgents.workingState.cancel", { defaultValue: "Cancel" })}
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="hosted-agent-working-state-delete-confirm"
                onClick={() =>
                  void runMutation(() => deleteMutation.mutateAsync({ agentId, factId: fact.id }))
                }
              >
                {t("adminDashboard.hostedAgents.workingState.delete", { defaultValue: "Delete" })}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {fact.active ? (
        <div className="space-y-0.5">
          <div className="text-sm font-semibold" data-testid="hosted-agent-working-state-value">
            {formatWorkingStateValue(fact.active, i18n.language)}
          </div>
          <div className="text-muted-foreground text-[11px]">
            {t("adminDashboard.hostedAgents.workingState.source", {
              defaultValue: "Source: {{source}}",
              source: fact.active.source,
            })}
          </div>
          <ProvenanceLine provenance={fact.active.provenance} />
        </div>
      ) : (
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.hostedAgents.workingState.noActiveValue", {
            defaultValue: "No confirmed value yet — review the proposals below.",
          })}
        </p>
      )}

      {draft ? (
        <div
          className="bg-muted/40 space-y-2 rounded-md border p-2"
          data-testid="hosted-agent-working-state-correct-form"
        >
          <div className="grid gap-2 sm:grid-cols-2">
            <Input
              data-testid="hosted-agent-working-state-correct-value"
              value={draft.value}
              placeholder={t("adminDashboard.hostedAgents.workingState.valuePlaceholder", {
                defaultValue: "Value",
              })}
              onChange={(event) => setDraft({ ...draft, value: event.target.value })}
            />
            <Input
              value={draft.unit}
              placeholder={t("adminDashboard.hostedAgents.workingState.unitPlaceholder", {
                defaultValue: "Unit (optional)",
              })}
              onChange={(event) => setDraft({ ...draft, unit: event.target.value })}
            />
          </div>
          <Input
            data-testid="hosted-agent-working-state-correct-source"
            value={draft.source}
            placeholder={t("adminDashboard.hostedAgents.workingState.sourcePlaceholder", {
              defaultValue: "Source — where does this value come from? (required)",
            })}
            onChange={(event) => setDraft({ ...draft, source: event.target.value })}
          />
          <p className="text-muted-foreground text-[11px]">
            {t("adminDashboard.hostedAgents.workingState.correctHint", {
              defaultValue:
                "A correction is applied immediately and pins the fact: the agent can no longer replace this value.",
            })}
          </p>
          <div className="flex gap-2">
            <Button
              data-testid="hosted-agent-working-state-correct-save"
              disabled={correctMutation.isPending}
              size="sm"
              type="button"
              onClick={() => void submitCorrection()}
            >
              {t("adminDashboard.hostedAgents.workingState.correctSave", {
                defaultValue: "Correct value",
              })}
            </Button>
            <Button size="sm" type="button" variant="ghost" onClick={() => setDraft(null)}>
              {t("adminDashboard.hostedAgents.workingState.cancel", { defaultValue: "Cancel" })}
            </Button>
          </div>
        </div>
      ) : null}

      {fact.proposals.length > 0 ? (
        <div className="space-y-2">
          <p className="text-muted-foreground text-[11px] font-semibold tracking-wider uppercase">
            {t("adminDashboard.hostedAgents.workingState.proposalsTitle", {
              defaultValue: "Pending proposals",
            })}
          </p>
          {fact.proposals.map((proposal) => (
            <div
              key={proposal.proposal_id}
              className="flex flex-wrap items-center gap-2 rounded-md border border-dashed p-2"
              data-testid="hosted-agent-working-state-proposal"
            >
              <div className="min-w-0 flex-1 space-y-0.5">
                <div className="text-sm">{formatWorkingStateValue(proposal, i18n.language)}</div>
                <div className="text-muted-foreground line-clamp-1 text-[11px]">
                  {t("adminDashboard.hostedAgents.workingState.source", {
                    defaultValue: "Source: {{source}}",
                    source: proposal.source,
                  })}
                </div>
                <ProvenanceLine provenance={proposal.provenance} />
              </div>
              <Button
                data-testid="hosted-agent-working-state-confirm"
                disabled={confirmMutation.isPending || fact.pinned}
                size="sm"
                type="button"
                variant="outline"
                title={
                  fact.pinned
                    ? t("adminDashboard.hostedAgents.workingState.confirmBlockedPinned", {
                        defaultValue:
                          "Pinned by a human correction — correct the value directly instead.",
                      })
                    : undefined
                }
                onClick={() =>
                  void runMutation(() =>
                    confirmMutation.mutateAsync({
                      agentId,
                      factId: fact.id,
                      proposalId: proposal.proposal_id,
                    })
                  )
                }
              >
                <Check aria-hidden="true" className="size-4" />
                {t("adminDashboard.hostedAgents.workingState.confirm", {
                  defaultValue: "Confirm",
                })}
              </Button>
              <Button
                data-testid="hosted-agent-working-state-reject"
                disabled={rejectMutation.isPending}
                size="sm"
                type="button"
                variant="ghost"
                onClick={() =>
                  void runMutation(() =>
                    rejectMutation.mutateAsync({
                      agentId,
                      factId: fact.id,
                      proposalId: proposal.proposal_id,
                    })
                  )
                }
              >
                <X aria-hidden="true" className="size-4" />
                {t("adminDashboard.hostedAgents.workingState.reject", { defaultValue: "Reject" })}
              </Button>
            </div>
          ))}
        </div>
      ) : null}

      {errorText ? (
        <p
          className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-xs"
          data-testid="hosted-agent-working-state-error"
        >
          {errorText}
        </p>
      ) : null}
    </div>
  )
}

/**
 * Working-state review panel (#3023), mounted under the Memory tab's mode
 * selector for SAVED agents: the facts the agent has established, their
 * pending proposals (confirm/reject), inline human correction (which pins),
 * and deletion. All actions apply to the server immediately.
 */
export function HostedAgentWorkingStatePanel({ agentId }: { agentId: string }) {
  const { t } = useTranslation()
  const factsQuery = useAdminHostedAgentWorkingStateQuery(agentId)
  const facts = factsQuery.data ?? []

  let body: React.ReactNode
  if (factsQuery.isLoading) {
    body = <Skeleton className="h-16 w-full" />
  } else if (factsQuery.isError) {
    body = (
      <p className="text-muted-foreground text-sm">
        {t("adminDashboard.hostedAgents.workingState.loadError", {
          defaultValue: "Working state is unavailable right now.",
        })}
      </p>
    )
  } else if (facts.length === 0) {
    body = (
      <p className="text-muted-foreground text-sm">
        {t("adminDashboard.hostedAgents.workingState.empty", {
          defaultValue:
            "No facts yet. When the agent proposes a value through its working-state tool, it appears here for review.",
        })}
      </p>
    )
  } else {
    body = (
      <div className="space-y-3">
        {facts.map((fact) => (
          <WorkingStateFactCard key={fact.id} agentId={agentId} fact={fact} />
        ))}
      </div>
    )
  }

  return (
    <div className="mt-6 space-y-3" data-testid="hosted-agent-working-state-panel">
      <div className="space-y-1">
        <h3 className="text-sm font-medium">
          {t("adminDashboard.hostedAgents.workingState.title", { defaultValue: "Working state" })}
        </h3>
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.hostedAgents.workingState.intro", {
            defaultValue:
              "Structured facts the agent has established, with source and provenance. The agent can only propose values — a human confirms or corrects them here. Unlike the fields above, these actions apply immediately and are not part of Save.",
          })}
        </p>
      </div>
      {body}
    </div>
  )
}
